import React, { useEffect, useRef, useState } from "react";
import { BsFillPauseFill, BsFillPlayFill, BsVolumeMuteFill, BsVolumeUpFill } from "react-icons/bs";
import WaveSurfer from "wavesurfer.js";
import { getMediaUrl } from "../../lib/utils";

const CommentVoice = ({ content }) => {
  const containerRef = useRef(null);
  const wavesurferRef = useRef(null);

  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasError, setHasError] = useState(false);

  const voiceUrl = getMediaUrl(content);

  useEffect(() => {
    if (!containerRef.current || !voiceUrl) return;

    setIsReady(false);
    setIsPlaying(false);
    setMuted(false);
    setCurrentTime(0);
    setDuration(0);
    setHasError(false);

    const wavesurfer = WaveSurfer.create({
      container: containerRef.current,
      waveColor: "#d1d5db",
      progressColor: "#f97316",
      cursorColor: "transparent",
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      height: 34,
      normalize: true,
      url: voiceUrl,
    });

    wavesurferRef.current = wavesurfer;

    wavesurfer.on("ready", () => {
      setDuration(wavesurfer.getDuration());
      setIsReady(true);
    });

    wavesurfer.on("play", () => setIsPlaying(true));
    wavesurfer.on("pause", () => setIsPlaying(false));

    wavesurfer.on("audioprocess", () => {
      setCurrentTime(wavesurfer.getCurrentTime());
    });

    wavesurfer.on("seeking", () => {
      setCurrentTime(wavesurfer.getCurrentTime());
    });

    wavesurfer.on("finish", () => {
      setIsPlaying(false);
      setCurrentTime(0);
    });

    wavesurfer.on("error", (error) => {
      console.error("Comment voice error:", error);
      setHasError(true);
      setIsReady(false);
      setIsPlaying(false);
    });

    return () => {
      wavesurfer.destroy();

      if (wavesurferRef.current === wavesurfer) {
        wavesurferRef.current = null;
      }
    };
  }, [voiceUrl]);

  if (!content) return null;

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds)) return "0:00";

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60).toString().padStart(2, "0");

    return `${minutes}:${remainingSeconds}`;
  };

  const togglePlay = async () => {
    if (!wavesurferRef.current || !isReady || hasError) return;

    try {
      await wavesurferRef.current.playPause();
    } catch (error) {
      console.error("Comment voice playback error:", error);
    }
  };

  const toggleMute = () => {
    if (!wavesurferRef.current) return;

    const nextMuted = !muted;

    wavesurferRef.current.setVolume(nextMuted ? 0 : 1);
    setMuted(nextMuted);
  };

  return (
    <div className="mt-2 w-full max-w-[460px]">
      <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50/80 px-3 py-3">
        <button type="button" onClick={togglePlay} disabled={!isReady || hasError} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white shadow-sm transition hover:bg-orange-600 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50" aria-label={isPlaying ? "Pause recording" : "Play recording"}>
          {isPlaying ? <BsFillPauseFill size={23} /> : <BsFillPlayFill className="ml-0.5" size={23} />}
        </button>

        <div className="min-w-0 flex-1">
          <div ref={containerRef} className="w-full" />

          <div className="mt-1 flex items-center justify-between">
            <span className="text-[11px] font-medium text-gray-400">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            <button type="button" onClick={toggleMute} disabled={!isReady || hasError} className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition hover:bg-white hover:text-gray-700 disabled:opacity-50" aria-label={muted ? "Unmute recording" : "Mute recording"}>
              {muted ? <BsVolumeMuteFill size={17} /> : <BsVolumeUpFill size={17} />}
            </button>
          </div>

          {hasError && <p className="mt-1 text-[11px] text-red-500">Unable to load this recording.</p>}
        </div>
      </div>
    </div>
  );
};

export default CommentVoice;